import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardHeader from "../CompanyComponents/DashboardHeaderRouter";
import api from "../../Utils/Axios";
import { toast } from "react-toastify";
import UrduKeyboard from "../../Components/UrduKeyboard";
import { useBackspaceNavigation } from "../../hooks/useBackspaceNavigation";

const normalizeText = (value) =>
  (value || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

function mapLikeToPlainObject(val) {
  if (val == null || typeof val !== 'object') return {};
  if (val instanceof Map) return Object.fromEntries(val);
  if (typeof val.forEach === 'function' && !Array.isArray(val)) {
    const plain = {};
    val.forEach((v, k) => {
      plain[k] = v;
    });
    return plain;
  }
  return { ...val };
}

const DEFAULT_BILL_UNITS = ['pali', 'paiti', 'units', 'bags', 'crates'];

const BILL_UNIT_LABELS_UR = {
  pali: 'پلی',
  paiti: 'پیتی',
  units: 'یونٹس',
  bags: 'بوریاں',
  crates: 'کریٹس'
};

function getMergedBillUnits(company) {
  const fromOpts = (company?.billItemOptions || []).map((o) => o.unit).filter(Boolean);
  const fromCompany = company?.units || [];
  return [...new Set([...DEFAULT_BILL_UNITS, ...fromCompany, ...fromOpts])];
}

function getDefaultItemNameUnitForNewBill(company) {
  if (!company) return { itemName: '', unit: '' };
  if (String(company.billItemFormMode || '').toLowerCase() === 'static') {
    return {
      itemName: company.billFormStaticItemName || '',
      unit: company.billFormStaticUnit || ''
    };
  }
  const first = company.billItemOptions?.[0];
  if (first && (first.name || first.unit)) {
    return { itemName: first.name || '', unit: first.unit || '' };
  }
  const firstItemName = (company.itemNames || []).find((n) => String(n || '').trim());
  if (firstItemName) {
    return { itemName: String(firstItemName).trim(), unit: '' };
  }
  return { itemName: '', unit: '' };
}

/** If deployed API omits new profile fields, fill from last dashboard cache (same session). */
function mergeCompanyProfileWithCachedBillFields(apiCompany) {
  if (!apiCompany || typeof apiCompany !== 'object') return apiCompany;
  const merged = { ...apiCompany };
  try {
    const raw = localStorage.getItem('companyData');
    if (!raw) return merged;
    const cached = JSON.parse(raw);
    if (!cached || typeof cached !== 'object') return merged;
    if (merged.billItemFormMode == null && cached.billItemFormMode != null) {
      merged.billItemFormMode = cached.billItemFormMode;
    }
    if (merged.billFormStaticItemName === undefined && cached.billFormStaticItemName !== undefined) {
      merged.billFormStaticItemName = cached.billFormStaticItemName;
    }
    if (merged.billFormStaticUnit === undefined && cached.billFormStaticUnit !== undefined) {
      merged.billFormStaticUnit = cached.billFormStaticUnit;
    }
    if ((!merged.billItemOptions || merged.billItemOptions.length === 0) && cached.billItemOptions?.length) {
      merged.billItemOptions = cached.billItemOptions;
    }
    if ((!merged.itemNames || merged.itemNames.length === 0) && cached.itemNames?.length) {
      merged.itemNames = cached.itemNames;
    }
    const cMode = String(cached.billItemFormMode || '').toLowerCase();
    const apiMode = String(merged.billItemFormMode || '').toLowerCase();
    if (
      cMode === 'static' &&
      (cached.billFormStaticItemName || cached.billFormStaticUnit) &&
      (apiMode !== 'static' || (!merged.billFormStaticItemName && !merged.billFormStaticUnit))
    ) {
      merged.billItemFormMode = 'static';
      merged.billFormStaticItemName = cached.billFormStaticItemName || merged.billFormStaticItemName || '';
      merged.billFormStaticUnit = cached.billFormStaticUnit || merged.billFormStaticUnit || '';
    }
  } catch (_) {
    /* ignore */
  }
  return merged;
}

const CreateBillPart2 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editBillId = searchParams.get('edit');
  const isEditMode = !!editBillId;

  // Backspace navigation to dashboard
  useBackspaceNavigation();

  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('ur');

  // Customer and Supplier states
  const [ledgerParties, setLedgerParties] = useState([]); // Customers
  const [supplierParties, setSupplierParties] = useState([]); // Suppliers
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showCustomerUrduKeyboard, setShowCustomerUrduKeyboard] = useState(false);
  const customerDropdownRef = useRef(null);
  const customerSearchInputRef = useRef(null);
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showSupplierUrduKeyboard, setShowSupplierUrduKeyboard] = useState(false);
  const supplierDropdownRef = useRef(null);
  const supplierSearchInputRef = useRef(null);
  // Track which purchase row has Urdu keyboard open
  const [purchaseRowUrduKeyboard, setPurchaseRowUrduKeyboard] = useState(null);
  const purchaseCustomerInputRefs = useRef({});
  const purchaseCustomerDropdownRefs = useRef({});
  const [purchaseRowHighlightedIndex, setPurchaseRowHighlightedIndex] = useState({}); // Track highlighted index for each row
  const purchaseCustomerListRefs = useRef({}); // Refs for customer list items in each row

  // Refs for tab navigation
  const dateRef = useRef(null);
  const serialNumberRef = useRef(null);
  const vehicleNumberRef = useRef(null);
  const itemNameRef = useRef(null);
  const unitSelectRef = useRef(null);
  const quantityRef = useRef(null);
  const commissionRef = useRef(null);
  const khamBakriRef = useRef(null);
  const akhrajatRef = useRef(null);
  const pakhtaBakriRef = useRef(null);
  const lagaRef = useRef(null);
  const naqdiRef = useRef(null);
  const karayaRef = useRef(null);
  const commissionDetailRef = useRef(null);
  const mazdooriRef = useRef(null);
  const mashianaRef = useRef(null);
  const barketFeeRef = useRef(null);


  // Form data
  const [formData, setFormData] = useState({
    date: new Date().toLocaleDateString('en-CA'),
    serialNumber: '1',
    voucherNumber: '1',
    supplierPartyId: '',
    supplierName: '',
    supplierNameUrdu: '',
    vehicleNumber: '',
    itemName: '',
    unit: '',
    quantity: '',
    commission: '',
    khamBakri: '',
    akhrajat: '',
    pakhtaBakri: '',
    naqdi: '',
    karaya: '',
    commissionDetail: '',
    mazdoori: '',
    mashiana: '',
    barketFee: '',
    purchaseTotalAmount: '', // Editable total amount for sale form
    editableTotalExpenses: '', // Editable total expenses (کل رقم)
    laga: '' // لاگا field
  });

  /** Always latest form values for save (avoids stale closure if async profile fetch races with user edits) */
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const [isSaving, setIsSaving] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const billDataFetchedRef = useRef(false);
  const originalSalesRef = useRef([]); // Track original sales when loading bill for edit

  // Commission logic - track if commission was manually edited
  const commissionManuallyEditedRef = useRef(false);
  // Track if purchaseTotalAmount was manually edited
  const purchaseTotalManuallyEditedRef = useRef(false);
  // Track if totalExpenses was manually edited
  const totalExpensesManuallyEditedRef = useRef(false);
  // Track which purchase row totals were manually edited (by row id)
  const purchaseRowTotalManuallyEditedRef = useRef({});

  // Purchase table rows (customer wise)
  const [purchaseRows, setPurchaseRows] = useState([
    {
      id: Date.now(),
      customerPartyId: '',
      customerName: '',
      customerNameUrdu: '',
      customerSearchTerm: '',
      showDropdown: false,
      detail: '',
      unit: '',
      dana: '',
      quantity: '',
      unitPrice: '',
      total: '',
      paidAmount: ''
    }
  ]);

  const mergedBillUnits = useMemo(() => getMergedBillUnits(company), [company]);

  const billItemSelectOptions = useMemo(() => {
    const names = new Set();
    for (const o of company?.billItemOptions || []) {
      const n = String(o?.name || '').trim();
      if (n) names.add(n);
    }
    for (const raw of company?.itemNames || []) {
      const n = String(raw || '').trim();
      if (n) names.add(n);
    }
    return [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [company?.billItemOptions, company?.itemNames]);

  const billItemSelectOptionsWithCurrent = useMemo(() => {
    const set = new Set(billItemSelectOptions);
    const cur = String(formData.itemName || '').trim();
    if (cur) set.add(cur);
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [billItemSelectOptions, formData.itemName]);

  const isStaticBillForm = useMemo(
    () => String(company?.billItemFormMode || '').toLowerCase() === 'static',
    [company?.billItemFormMode]
  );

  useEffect(() => {
    if (!company || editBillId) return;
    if (String(company.billItemFormMode || '').toLowerCase() !== 'static') return;
    const ni = company.billFormStaticItemName || '';
    const nu = company.billFormStaticUnit || '';
    setFormData((prev) => ({ ...prev, itemName: ni, unit: nu }));
  }, [
    company?.billItemFormMode,
    company?.billFormStaticItemName,
    company?.billFormStaticUnit,
    editBillId,
    company
  ]);

  useEffect(() => {
    const loadData = async () => {
      // Reset form data when entering create mode (not edit mode)
      if (!editBillId) {
        billDataFetchedRef.current = false; // Reset ref for create mode
        setFormData({
          date: new Date().toLocaleDateString('en-CA'),
          serialNumber: '1',
          voucherNumber: '1',
          supplierPartyId: '',
          supplierName: '',
          supplierNameUrdu: '',
          vehicleNumber: '',
          itemName: '',
          unit: '',
          quantity: '',
          commission: '',
          khamBakri: '',
          laga: '',
          akhrajat: '',
          pakhtaBakri: '',
          naqdi: '',
          karaya: '',
          commissionDetail: '',
          mazdoori: '',
          mashiana: '',
          barketFee: '',
          purchaseTotalAmount: '',
          editableTotalExpenses: ''
        });
        setPurchaseRows([{
          id: Date.now(),
          customerPartyId: '',
          customerName: '',
          customerNameUrdu: '',
          customerSearchTerm: '',
          showDropdown: false,
          detail: '',
          unit: '',
          dana: '',
          quantity: '',
          unitPrice: '',
          total: '',
          paidAmount: ''
        }]);
        setSupplierSearchTerm('');
        setShowSupplierDropdown(false);
        setShowSupplierUrduKeyboard(false);
      } else {
        billDataFetchedRef.current = false; // Reset ref for edit mode
      }
      await fetchCompanyProfile();
      setDataLoaded(true);
    };
    loadData();
  }, [editBillId]); // Re-run when editBillId changes (when navigating between create/edit modes)

  // Fetch bill data after company data and parties are loaded (only once)
  useEffect(() => {
    if (editBillId && dataLoaded && !billDataFetchedRef.current) {
      billDataFetchedRef.current = true;
      fetchBillData(editBillId);
    }
  }, [editBillId, dataLoaded]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target)) {
        setShowSupplierDropdown(false);
      }
      // Close purchase rows dropdowns
      purchaseRows.forEach(row => {
        const dropdownRef = purchaseCustomerDropdownRefs.current[row.id];
        if (dropdownRef && !dropdownRef.contains(event.target)) {
          setPurchaseRows(prev =>
            prev.map(r => r.id === row.id ? { ...r, showDropdown: false } : r)
          );
        }
      });
    };

    const hasOpenDropdowns = showCustomerDropdown || showSupplierDropdown ||
      purchaseRows.some(row => row.showDropdown);

    if (hasOpenDropdowns) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown, showSupplierDropdown, purchaseRows]);

  // Auto-scroll to show dropdown when it opens in purchase table - Optimized
  useEffect(() => {
    const rowsWithOpenDropdown = purchaseRows.filter(row => row.showDropdown);
    if (rowsWithOpenDropdown.length === 0) return;

    rowsWithOpenDropdown.forEach(row => {
      const inputElement = purchaseCustomerInputRefs.current[row.id];
      if (!inputElement) return;

      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        const rect = inputElement.getBoundingClientRect();
        const dropdownHeight = 300;
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;

        if (spaceBelow < dropdownHeight + 20) {
          inputElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        } else {
          inputElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
      });
    });
  }, [purchaseRows.map(r => `${r.id}-${r.showDropdown}`).join(',')]);

  // Auto-close Urdu keyboard when English character is typed in customerSearchTerm
  // Auto-open when field becomes empty
  useEffect(() => {
    if (customerSearchTerm === undefined || customerSearchTerm === null) return;

    const value = String(customerSearchTerm);

    // Check if field contains any English characters
    const hasEnglishChar = /[a-zA-Z]/.test(value);

    if (hasEnglishChar) {
      // Close Urdu keyboard if English character is found
      setShowCustomerUrduKeyboard(false);
    } else if (value.trim() === '' && showCustomerUrduKeyboard === false) {
      // Open Urdu keyboard if field becomes empty and keyboard is not already open
      // Only open if the field is focused (to avoid opening when form is reset)
      if (customerSearchInputRef.current && customerSearchInputRef.current === document.activeElement) {
        setShowCustomerUrduKeyboard(true);
      }
    }
  }, [customerSearchTerm, showCustomerUrduKeyboard]);

  // Auto-close Urdu keyboard when English character is typed in supplierSearchTerm
  // Auto-open when field becomes empty
  useEffect(() => {
    if (supplierSearchTerm === undefined || supplierSearchTerm === null) return;

    const value = String(supplierSearchTerm);

    // Check if field contains any English characters
    const hasEnglishChar = /[a-zA-Z]/.test(value);

    if (hasEnglishChar) {
      // Close Urdu keyboard if English character is found
      setShowSupplierUrduKeyboard(false);
    } else if (value.trim() === '' && showSupplierUrduKeyboard === false) {
      // Open Urdu keyboard if field becomes empty and keyboard is not already open
      // Only open if the field is focused (to avoid opening when form is reset)
      if (supplierSearchInputRef.current && supplierSearchInputRef.current === document.activeElement) {
        setShowSupplierUrduKeyboard(true);
      }
    }
  }, [supplierSearchTerm, showSupplierUrduKeyboard]);

  // Auto-close Urdu keyboard when English character is typed in purchase row customerSearchTerm
  // Auto-open when field becomes empty
  useEffect(() => {
    purchaseRows.forEach(row => {
      if (row.customerSearchTerm === undefined || row.customerSearchTerm === null) return;

      const value = String(row.customerSearchTerm);

      // Check if field contains any English characters
      const hasEnglishChar = /[a-zA-Z]/.test(value);

      if (hasEnglishChar) {
        // Close Urdu keyboard if English character is found
        if (purchaseRowUrduKeyboard === row.id) {
          setPurchaseRowUrduKeyboard(null);
        }
      } else if (value.trim() === '' && purchaseRowUrduKeyboard !== row.id) {
        // Open Urdu keyboard if field becomes empty and keyboard is not already open for this row
        // Only open if the field is focused (to avoid opening when form is reset)
        const inputElement = purchaseCustomerInputRefs.current[row.id];
        if (inputElement && inputElement === document.activeElement) {
          setPurchaseRowUrduKeyboard(row.id);
        }
      }
    });
  }, [purchaseRows.map(r => r.customerSearchTerm).join(','), purchaseRowUrduKeyboard]);

  const fetchCompanyProfile = async () => {
    try {
      const token = localStorage.getItem('companyToken');
      if (!token) {
        navigate('/company/login');
        return;
      }

      const response = await api.get('/company/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const companyPayload = mergeCompanyProfileWithCachedBillFields(response.data.company);
      setCompany(companyPayload);
      try {
        localStorage.setItem('companyData', JSON.stringify(companyPayload));
      } catch (_) {
        /* ignore */
      }
      const lang = companyPayload?.websiteLanguage || companyPayload?.language || 'ur';
      setCompanyLanguage(lang);
      localStorage.setItem('companyLanguage', lang);

      // Fetch customers and suppliers
      await fetchLedgerParties();
      await fetchSupplierParties();

      const itemUnitDefaults = getDefaultItemNameUnitForNewBill(companyPayload);

      // Auto-generate serial number only if NOT in edit mode
      if (!editBillId) {
        // Always ask backend for all bills and compute next serial (same as Main Dashboard)
        try {
          const billsResponse = await api.get("/bill/company/my-bills", {
            headers: { Authorization: `Bearer ${token}` }
          });

          let nextSerial = 1;
          if (billsResponse.data.bills && billsResponse.data.bills.length > 0) {
            // Find highest valid numeric serial number among existing bills
            // Filter out empty strings and non-numeric values
            const validSerials = billsResponse.data.bills
              .map(bill => {
                const sn = bill.serialNumber?.toString().trim() || '';
                const num = parseInt(sn, 10);
                return !isNaN(num) && num > 0 ? num : null;
              })
              .filter(num => num !== null);

            if (validSerials.length > 0) {
              // Find the maximum serial number
              const maxSerial = Math.max(...validSerials);
              nextSerial = maxSerial + 1;
            } else {
              // If no valid numeric serials, use count-based sequence
              nextSerial = billsResponse.data.bills.length + 1;
            }
          }

          setFormData((prev) => {
            const next = {
              ...prev,
              serialNumber: String(nextSerial),
              voucherNumber: String(nextSerial)
            };
            if (!String(prev.itemName || '').trim()) {
              next.itemName = itemUnitDefaults.itemName;
              next.unit = itemUnitDefaults.unit;
            }
            return next;
          });
        } catch (error) {
          console.error('Error fetching bills for auto-increment:', error);
          setFormData((prev) => {
            if (String(prev.itemName || '').trim()) return prev;
            return {
              ...prev,
              itemName: itemUnitDefaults.itemName,
              unit: itemUnitDefaults.unit
            };
          });
        }
      }
    } catch (error) {
      console.error('Fetch company error:', error);
      toast.error('Failed to load company data');
      setIsLoading(false);
    } finally {
      // Create mode: hide spinner after profile. Edit mode: keep loading until fetchBillData finishes
      // so the user cannot Save with stale formData / formDataRef from a previous screen.
      if (!editBillId) {
        setIsLoading(false);
      }
    }
  };

  const fetchLedgerParties = async () => {
    try {
      const token = localStorage.getItem('companyToken');
      const response = await api.get("/ledger/parties?type=customer", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLedgerParties(response.data.parties || []);
    } catch (error) {
      console.error("Fetch ledger parties error:", error);
    }
  };

  const fetchSupplierParties = async () => {
    try {
      const token = localStorage.getItem('companyToken');
      const response = await api.get("/ledger/parties?type=supplier", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSupplierParties(response.data.parties || []);
    } catch (error) {
      console.error("Fetch supplier parties error:", error);
    }
  };


  // Fetch bill data for edit mode
  const fetchBillData = async (billId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('companyToken');
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        navigate('/company/login');
        return;
      }

      const response = await api.get(`/bill/company/${billId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const bill = response.data.bill;
      if (!bill) {
        toast.error(companyLanguage === 'ur' ? 'بل نہیں ملا' : 'Bill not found');
        navigate('/company/dashboard-part2');
        setIsLoading(false);
        return;
      }

      const expensesObj = mapLikeToPlainObject(bill.expenses);
      const salesPlain = mapLikeToPlainObject(bill.sales);

      // Map bill data to formData structure
      setFormData(prev => ({
        ...prev,
        date: bill.date ? new Date(bill.date).toLocaleDateString('en-CA') : new Date().toLocaleDateString('en-CA'),
        serialNumber: bill.serialNumber || '1',
        voucherNumber: bill.voucherNumber || bill.serialNumber || '1',
        supplierPartyId: bill.customerPartyId || '', // Supplier is stored as customer in bill
        supplierName: bill.customerName || '',
        supplierNameUrdu: bill.customerNameUrdu || '',
        vehicleNumber: expensesObj['ActualVehicle'] || expensesObj['Vehicle'] || '',
        itemName: expensesObj['ItemName'] || expensesObj['itemName'] || '',
        unit: expensesObj['ItemUnit'] || expensesObj['itemUnit'] || '',
        quantity: expensesObj['ItemQuantity'] || expensesObj['itemQuantity'] || '',
        // Map expense fields
        naqdi: expensesObj['نقدی'] || expensesObj['Cash'] || '',
        karaya: expensesObj['کرایه'] || expensesObj['Rent'] || '',
        commissionDetail: expensesObj['کمیشن'] || expensesObj['Commission'] || '',
        mazdoori: expensesObj['مزدوری'] || expensesObj['Labor'] || expensesObj['Wages'] || '',
        mashiana: expensesObj['مشیانه'] || expensesObj['مشیانہ'] || expensesObj['Miscellaneous'] || expensesObj['Mashiyana'] || '',
        barketFee: expensesObj['مارکیٹ فیس'] || expensesObj['Market Fee'] || '',
        // Calculated fields (load from bill if available)
        khamBakri: expensesObj['خام بکری'] || salesPlain['Raw Sale'] || salesPlain.rawSale || '',
        laga: expensesObj['لاگا'] || '',
        akhrajat: expensesObj['اخراجات'] || '',
        pakhtaBakri: expensesObj['پختہ بکری'] || ''
      }));

      // Set supplier search term
      if (bill.customerNameUrdu || bill.customerName) {
        setSupplierSearchTerm(bill.customerNameUrdu || bill.customerName);
      }

      // Calculate commission percentage if commissionDetail exists
      const commissionDetail = expensesObj['کمیشن'] || expensesObj['Commission'] || 0;
      const rawSale = expensesObj['خام بکری'] || salesPlain['Raw Sale'] || salesPlain.rawSale || 0;
      if (commissionDetail && rawSale) {
        const commissionNum = typeof commissionDetail === 'string' ? parseFloat(commissionDetail) : commissionDetail;
        const rawSaleNum = typeof rawSale === 'string' ? parseFloat(rawSale) : rawSale;
        if (rawSaleNum > 0 && commissionNum > 0) {
          const percentage = (commissionNum / rawSaleNum) * 100;
          setFormData(prev => ({
            ...prev,
            commission: percentage.toFixed(2)
          }));
        }
      }

      // Fetch sales associated with this bill (matching serialNumber)
      try {
        const salesResponse = await api.get('/sale/company/all', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allSales = salesResponse.data.sales || [];

        // Filter sales by bill's serialNumber
        const billSerialNumber = bill.serialNumber || '';
        const billSales = allSales.filter(sale =>
          sale.serialNumber && String(sale.serialNumber).trim() === String(billSerialNumber).trim()
        );

        console.log('Bill serialNumber:', billSerialNumber);
        console.log('Found sales for this bill:', billSales.length);

        // Store original sales for tracking deletions
        originalSalesRef.current = billSales.map(sale => sale._id);

        // Populate purchaseRows with sales data
        if (billSales.length > 0) {
          const salesRows = billSales.map((sale, index) => {
            // Get first item from sale for unit, quantity, unitPrice
            const firstItem = sale.items && sale.items.length > 0 ? sale.items[0] : null;

            return {
              id: Date.now() + index,
              saleId: sale._id, // Store saleId to track existing sales
              customerPartyId: sale.customerPartyId?._id || sale.customerPartyId || '',
              customerName: sale.sellerName || sale.customerPartyId?.name || '',
              customerNameUrdu: sale.sellerNameUrdu || sale.customerPartyId?.nameUrdu || sale.customerPartyId?.name || '',
              customerSearchTerm: '',
              showDropdown: false,
              detail: firstItem?.description || firstItem?.descriptionUrdu || '',
              unit: firstItem?.unit || '',
              dana: firstItem?.dana || '',
              quantity: firstItem?.quantity || '',
              unitPrice: firstItem?.ratePerUnit || '',
              total: sale.totalAmount || firstItem?.lineTotal || '',
              paidAmount: sale.amountPaid || ''
            };
          });

          setPurchaseRows(salesRows);
          console.log('Populated purchaseRows with sales:', salesRows.length);
        } else {
          // If no sales found, keep default empty row
          console.log('No sales found for this bill, keeping default row');
        }
      } catch (salesError) {
        console.error("Fetch sales error:", salesError);
        // Don't show error to user, just log it - sales are optional
      }

    } catch (error) {
      console.error("Fetch bill error:", error);
      toast.error(companyLanguage === 'ur' ? 'بل لوڈ کرنے میں خرابی' : 'Failed to load bill');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (partyId) => {
    const party = ledgerParties.find(p => p._id === partyId);
    if (party) {
      setFormData(prev => ({
        ...prev,
        customerPartyId: partyId,
        customerName: party.name,
        customerNameUrdu: party.nameUrdu || party.name,
        customerPhone: party.phone || ''
      }));
      setCustomerSearchTerm('');
      setShowCustomerDropdown(false);
    } else {
      setFormData(prev => ({
        ...prev,
        customerPartyId: '',
        customerName: '',
        customerNameUrdu: '',
        customerPhone: ''
      }));
    }
  };

  // Handle supplier selection
  const handleSupplierSelect = (partyId) => {
    const party = supplierParties.find(p => p._id === partyId);
    if (party) {
      setFormData(prev => ({
        ...prev,
        supplierPartyId: partyId,
        supplierName: party.name,
        supplierNameUrdu: party.nameUrdu || party.name
      }));
      setSupplierSearchTerm(''); // Clear search term when selected (same as recovery)
      setShowSupplierDropdown(false);
      setShowSupplierUrduKeyboard(false); // Close Urdu keyboard when selected (same as recovery)
    } else {
      setFormData(prev => ({
        ...prev,
        supplierPartyId: '',
        supplierName: '',
        supplierNameUrdu: ''
      }));
      setSupplierSearchTerm('');
    }
  };

  // Handle Urdu keyboard input for customer search
  const handleCustomerSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setCustomerSearchTerm(prev => prev ? prev.slice(0, -1) : '');
    } else {
      setCustomerSearchTerm(prev => (prev || '') + key);
      setShowCustomerDropdown(true);
    }
  };

  // Toggle Urdu keyboard on customer search input click
  // Open keyboard if field is empty, otherwise toggle
  const handleCustomerSearchInputClick = () => {
    if (!customerSearchTerm || customerSearchTerm.trim() === '') {
      // If field is empty, open keyboard
      setShowCustomerUrduKeyboard(true);
    } else {
      // Otherwise toggle
      setShowCustomerUrduKeyboard(prev => !prev);
    }
  };

  // Handle Urdu keyboard input for supplier search
  const handleSupplierSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setSupplierSearchTerm(prev => prev ? prev.slice(0, -1) : '');
    } else {
      setSupplierSearchTerm(prev => (prev || '') + key);
      setShowSupplierDropdown(true);
    }
  };

  // Open Urdu keyboard on supplier search input click
  // Open keyboard if field is empty, otherwise toggle
  const handleSupplierSearchInputClick = () => {
    if (!supplierSearchTerm || supplierSearchTerm.trim() === '') {
      // If field is empty, open keyboard
      setShowSupplierUrduKeyboard(true);
    } else {
      // Otherwise toggle
      setShowSupplierUrduKeyboard(prev => !prev);
    }
  };

  // Handle Urdu keyboard input for purchase table customer search
  const handlePurchaseCustomerUrduKeyPress = (key, rowId) => {
    if (key === 'backspace') {
      setPurchaseRows(prev =>
        prev.map(row => {
          if (row.id !== rowId) return row;
          return {
            ...row,
            customerSearchTerm: row.customerSearchTerm ? row.customerSearchTerm.slice(0, -1) : ''
          };
        })
      );
    } else {
      setPurchaseRows(prev =>
        prev.map(row => {
          if (row.id !== rowId) return row;
          return {
            ...row,
            customerSearchTerm: (row.customerSearchTerm || '') + key,
            showDropdown: true
          };
        })
      );
    }
  };

  // Purchase table row helpers
  const handleAddPurchaseRow = () => {
    setPurchaseRows(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        customerPartyId: '',
        customerName: '',
        customerNameUrdu: '',
        customerSearchTerm: '',
        showDropdown: false,
        detail: formData.itemName || '', // Auto-populate with item name from upper section
        unit: formData.unit || '', // Auto-populate with unit from upper section
        dana: '',
        quantity: '',
        unitPrice: '',
        total: '',
        paidAmount: ''
      }
    ]);
  };

  const handleDeletePurchaseRow = async (rowId) => {
    if (purchaseRows.length > 1) {
      const rowToDelete = purchaseRows.find(row => row.id === rowId);

      // If row has a saleId, delete the sale from backend
      if (rowToDelete?.saleId) {
        try {
          const token = localStorage.getItem('companyToken');
          if (token) {
            await api.delete(`/sale/company/${rowToDelete.saleId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(companyLanguage === 'ur' ? 'سیل ختم ہو گئی' : 'Sale deleted successfully');
          }
        } catch (error) {
          console.error('Failed to delete sale:', error);
          toast.error(companyLanguage === 'ur' ? 'سیل ختم کرنے میں خرابی' : 'Failed to delete sale');
          // Still remove the row from UI even if backend delete fails
        }
      }

      // Remove row from state
      setPurchaseRows(prev => prev.filter(row => row.id !== rowId));
    } else {
      toast.warning(companyLanguage === 'ur' ? 'کم از کم ایک قطار ضروری ہے' : 'At least one row is required');
    }
  };

  const handlePurchaseRowChange = (rowId, field, value) => {
    setPurchaseRows(prev =>
      prev.map(row => {
        if (row.id !== rowId) return row;
        const updated = { ...row, [field]: value };

        // If total is being manually edited, mark it as manually edited
        if (field === 'total') {
          purchaseRowTotalManuallyEditedRef.current[rowId] = true;
        }

        // Auto-calculate total only if it hasn't been manually edited
        if ((field === 'quantity' || field === 'unitPrice') && !purchaseRowTotalManuallyEditedRef.current[rowId]) {
          const qtyValue = field === 'quantity' ? value : row.quantity;
          const priceValue = field === 'unitPrice' ? value : row.unitPrice;
          const qty = parseFloat(qtyValue) || 0;
          const price = parseFloat(priceValue) || 0;
          const calculatedTotal = qty * price;
          // Show empty string if total is 0 or if either field is empty
          updated.total = (qtyValue && priceValue && calculatedTotal > 0) ? calculatedTotal.toString() : '';
        }
        return updated;
      })
    );
  };

  // Handle commission percentage change (based on Raw Sale - khamBakri)
  const handleCommissionPercentageChange = (value) => {
    commissionManuallyEditedRef.current = false; // Reset flag when percentage is changed

    // Calculate and update commission amount from Raw Sale
    if (value && value !== '' && !isNaN(parseFloat(value))) {
      const percentage = parseFloat(value);
      if (percentage > 0) {
        const rawSaleValue = formData.khamBakri || '';
        const rawSale = rawSaleValue === '' || rawSaleValue === null || rawSaleValue === undefined ? 0 : (typeof rawSaleValue === 'number' ? rawSaleValue : parseFloat(rawSaleValue) || 0);

        if (rawSale > 0) {
          const commissionAmount = (rawSale * percentage) / 100;
          setFormData(prev => ({
            ...prev,
            commission: value,
            commissionDetail: commissionAmount.toString()
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            commission: value,
            commissionDetail: ''
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          commission: value,
          commissionDetail: ''
        }));
      }
    } else {
      // Clear commission if percentage is cleared
      setFormData(prev => ({
        ...prev,
        commission: value,
        commissionDetail: ''
      }));
    }
  };

  // Handle commission amount change (when manually edited)
  const handleCommissionAmountChange = (value) => {
    commissionManuallyEditedRef.current = true;

    // If user manually edits commission amount, calculate and update percentage
    const commissionAmount = value === '' ? 0 : (typeof value === 'number' ? value : parseFloat(value) || 0);
    const rawSaleValue = formData.khamBakri || '';
    const rawSale = rawSaleValue === '' || rawSaleValue === null || rawSaleValue === undefined ? 0 : (typeof rawSaleValue === 'number' ? rawSaleValue : parseFloat(rawSaleValue) || 0);

    if (rawSale > 0 && commissionAmount > 0) {
      // Calculate percentage: (commission / rawSale) * 100
      const calculatedPercentage = (commissionAmount / rawSale) * 100;
      setFormData(prev => ({
        ...prev,
        commission: calculatedPercentage.toFixed(2),
        commissionDetail: value
      }));
    } else {
      // Clear percentage if commission is 0 or raw sale is 0
      setFormData(prev => ({
        ...prev,
        commission: '',
        commissionDetail: value
      }));
    }
  };

  // Auto-update commission when Raw Sale (khamBakri) changes (only if percentage is set and commission wasn't manually edited)
  useEffect(() => {
    // Skip if commission was manually edited
    if (commissionManuallyEditedRef.current) {
      return;
    }

    const commissionPercentage = formData.commission || '';
    if (commissionPercentage && commissionPercentage !== '' && !isNaN(parseFloat(commissionPercentage))) {
      const percentage = parseFloat(commissionPercentage);
      if (percentage > 0) {
        const rawSaleValue = formData.khamBakri || '';
        const rawSale = rawSaleValue === '' || rawSaleValue === null || rawSaleValue === undefined ? 0 : (typeof rawSaleValue === 'number' ? rawSaleValue : parseFloat(rawSaleValue) || 0);

        if (rawSale > 0) {
          const commissionAmount = (rawSale * percentage) / 100;
          // Only update if different to avoid infinite loops
          const currentCommission = formData.commissionDetail || '';
          const currentCommissionNum = currentCommission === '' || currentCommission === null || currentCommission === undefined ? 0 : (typeof currentCommission === 'number' ? currentCommission : parseFloat(currentCommission) || 0);

          if (Math.abs(currentCommissionNum - commissionAmount) > 0.01) {
            setFormData(prev => ({
              ...prev,
              commissionDetail: commissionAmount.toString()
            }));
          }
        }
      }
    }
  }, [formData.commission, formData.khamBakri]);

  // Auto-populate item name and unit from upper section to all purchase rows - Optimized
  useEffect(() => {
    if (!formData.itemName && !formData.unit) return;

    setPurchaseRows(prev => {
      const hasChanges = prev.some(row =>
        (formData.itemName && row.detail !== formData.itemName) ||
        (formData.unit && row.unit !== formData.unit)
      );

      if (!hasChanges) return prev;

      return prev.map(row => ({
        ...row,
        detail: formData.itemName || row.detail,
        unit: formData.unit || row.unit
      }));
    });
  }, [formData.itemName, formData.unit]);

  // Calculate totals (same logic as CreateBill.jsx) - Memoized for performance
  // First calculate expenses excluding commission (to avoid circular dependency)
  const totalExpensesExcludingCommission = useMemo(() => {
    let total = 0;
    // Add all expense fields except commission
    const expenseFields = [
      formData.naqdi,
      formData.karaya,
      formData.mazdoori,
      formData.mashiana,
      formData.barketFee
    ];

    expenseFields.forEach(value => {
      const numValue = value === '' || value === null || value === undefined ? 0 : (typeof value === 'number' ? value : parseFloat(value) || 0);
      if (!isNaN(numValue)) {
        total += numValue;
      }
    });

    return total;
  }, [formData.naqdi, formData.karaya, formData.mazdoori, formData.mashiana, formData.barketFee]);

  // Calculate total expenses (including commission) - Memoized
  const calculatedTotalExpenses = useMemo(() => {
    const commissionValue = formData.commissionDetail || '';
    const commission = commissionValue === '' || commissionValue === null || commissionValue === undefined ? 0 : (typeof commissionValue === 'number' ? commissionValue : parseFloat(commissionValue) || 0);
    return totalExpensesExcludingCommission + commission;
  }, [totalExpensesExcludingCommission, formData.commissionDetail]);

  // Use editable value if manually edited, otherwise use calculated value
  const totalExpenses = useMemo(() => {
    if (totalExpensesManuallyEditedRef.current && formData.editableTotalExpenses) {
      const manualValue = parseFloat(formData.editableTotalExpenses.replace(/,/g, '')) || 0;
      return manualValue;
    }
    return calculatedTotalExpenses;
  }, [calculatedTotalExpenses, formData.editableTotalExpenses]);

  // Auto-update editable totalExpenses when calculated value changes (only if not manually edited)
  useEffect(() => {
    if (!totalExpensesManuallyEditedRef.current && calculatedTotalExpenses > 0) {
      const calculatedValue = calculatedTotalExpenses.toLocaleString();
      setFormData(prev => ({
        ...prev,
        editableTotalExpenses: calculatedValue
      }));
    }
  }, [calculatedTotalExpenses]);

  // Calculate net sale (پختہ بکری) - Memoized
  const netSale = useMemo(() => {
    const rawSaleValue = formData.khamBakri || '';
    const rawSale = rawSaleValue === '' || rawSaleValue === null || rawSaleValue === undefined ? 0 : (typeof rawSaleValue === 'number' ? rawSaleValue : parseFloat(rawSaleValue) || 0);
    return rawSale - totalExpenses;
  }, [formData.khamBakri, totalExpenses]);

  // Calculate totals from purchase rows - Memoized
  const purchaseTotalAmount = useMemo(() => {
    return purchaseRows.reduce((sum, row) => {
      const total = row.total === '' || row.total === null || row.total === undefined ? 0 : (typeof row.total === 'number' ? row.total : parseFloat(row.total) || 0);
      return sum + total;
    }, 0);
  }, [purchaseRows]);

  const purchaseTotalPaidAmount = useMemo(() => {
    return purchaseRows.reduce((sum, row) => {
      const paid = row.paidAmount === '' || row.paidAmount === null || row.paidAmount === undefined ? 0 : (typeof row.paidAmount === 'number' ? row.paidAmount : parseFloat(row.paidAmount) || 0);
      return sum + paid;
    }, 0);
  }, [purchaseRows]);

  // Auto-update editable purchaseTotalAmount when calculated value changes (only if not manually edited)
  useEffect(() => {
    if (!purchaseTotalManuallyEditedRef.current && purchaseTotalAmount > 0) {
      const calculatedValue = purchaseTotalAmount.toFixed(2);
      setFormData(prev => ({
        ...prev,
        purchaseTotalAmount: calculatedValue
      }));
    }
  }, [purchaseTotalAmount]);

  // Memoized filtered suppliers for performance
  const filteredSuppliers = useMemo(() => {
    if (!supplierSearchTerm) return supplierParties;
    const searchLower = supplierSearchTerm.toLowerCase();
    return supplierParties.filter(party => {
      const nameLower = (party.name || '').toLowerCase();
      const nameUrduLower = (party.nameUrdu || '').toLowerCase();
      return nameLower.includes(searchLower) || nameUrduLower.includes(searchLower);
    });
  }, [supplierParties, supplierSearchTerm]);

  // Memoized filtered customers function for each row - optimized
  const getFilteredCustomersForRow = useCallback((searchTerm) => {
    if (!searchTerm) return ledgerParties;
    const searchLower = searchTerm.toLowerCase();
    return ledgerParties.filter(party => {
      const nameLower = (party.name || '').toLowerCase();
      const nameUrduLower = (party.nameUrdu || '').toLowerCase();
      return nameLower.includes(searchLower) || nameUrduLower.includes(searchLower);
    });
  }, [ledgerParties]);

  const handlePurchaseCustomerSelect = (rowId, partyId) => {
    const party = ledgerParties.find(p => p._id === partyId);
    if (party) {
      setPurchaseRows(prev =>
        prev.map(row => {
          if (row.id !== rowId) return row;
          return {
            ...row,
            customerPartyId: partyId,
            customerName: party.name,
            customerNameUrdu: party.nameUrdu || party.name,
            customerSearchTerm: '', // Clear search term when selected (same as recovery)
            showDropdown: false
          };
        })
      );
      setPurchaseRowUrduKeyboard(null); // Close Urdu keyboard when selected (same as recovery)
    }
  };

  const handlePurchaseCustomerSearch = (rowId, value) => {
    // Same as recovery: just set search term and open dropdown
    setPurchaseRows(prev =>
      prev.map(row => {
        if (row.id !== rowId) return row;
        return {
          ...row,
          customerSearchTerm: value,
          showDropdown: true
        };
      })
    );
    // Reset highlighted index when search changes
    setPurchaseRowHighlightedIndex(prev => ({
      ...prev,
      [rowId]: -1
    }));
  };

  const togglePurchaseDropdown = (rowId, show) => {
    setPurchaseRows(prev =>
      prev.map(row =>
        row.id === rowId ? { ...row, showDropdown: show } : row
      )
    );
  };

  // Handle Enter key to move to next field
  const handleEnterKey = (e, nextRef) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  // Handle Enter key in table - move to next field in same row or next row
  const handleTableEnterKey = (e, currentRowIndex, currentFieldIndex) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const table = e.target.closest('table');
      if (!table) return;

      const rows = table.querySelectorAll('tbody tr');
      const currentRow = rows[currentRowIndex];
      if (!currentRow) return;

      const inputs = currentRow.querySelectorAll('input, select');
      const nextFieldIndex = currentFieldIndex + 1;

      // If we're on the paidAmount field (last input field, index 7), add a new row
      if (currentFieldIndex === 7) {
        // Add new row
        handleAddPurchaseRow();
        // Focus on the first input of the new row after a short delay
        setTimeout(() => {
          const newRows = table.querySelectorAll('tbody tr');
          const newRowIndex = currentRowIndex + 1;
          if (newRowIndex < newRows.length) {
            const newRowInputs = newRows[newRowIndex].querySelectorAll('input, select');
            if (newRowInputs.length > 0) {
              newRowInputs[0].focus();
            }
          }
        }, 100);
        return;
      }

      // If there's a next field in the same row, focus it
      if (nextFieldIndex < inputs.length) {
        inputs[nextFieldIndex].focus();
      } else {
        // Move to next row's first input, or if last row, stay on current field
        const nextRowIndex = currentRowIndex + 1;
        if (nextRowIndex < rows.length) {
          const nextRowInputs = rows[nextRowIndex].querySelectorAll('input, select');
          if (nextRowInputs.length > 0) {
            nextRowInputs[0].focus();
          }
        }
      }
    }
  };

  // Handle save - create bill and sales
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const token = localStorage.getItem('companyToken');
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم لاگ ان کریں' : 'Please login');
        navigate('/company/login');
        return;
      }

      const fd = formDataRef.current;

      // Validate supplier is selected
      if (!fd.supplierPartyId) {
        toast.error(companyLanguage === 'ur' ? 'براہ کرم سپلائر منتخب کریں' : 'Please select a supplier');
        setIsSaving(false);
        return;
      }

      // Prepare expenses object for bill
      // Note: ActualVehicle is used for گاڑی نمبر (Vehicle Number) as per CreateBill.jsx pattern
      // First, calculate total expenses WITHOUT including laga or akhrajat
      const expenseFieldsForCalculation = {
        'نقدی': parseFloat(fd.naqdi) || 0,
        'کرایه': parseFloat(fd.karaya) || 0,
        'کمیشن': parseFloat(fd.commissionDetail) || 0,
        'مزدوری': parseFloat(fd.mazdoori) || 0,
        'مشیانه': parseFloat(fd.mashiana) || 0,
        'مارکیٹ فیس': parseFloat(fd.barketFee) || 0
      };

      // Calculate total expenses - ONLY include actual expense fields (EXCLUDE laga)
      const totalExpenses = Object.values(expenseFieldsForCalculation).reduce((sum, val) => {
        return sum + (parseFloat(val) || 0);
      }, 0);

      // Now create the full expenses object with calculated akhrajat (excluding laga)
      const expenses = {
        'ActualVehicle': fd.vehicleNumber || '', // گاڑی نمبر
        'ItemName': fd.itemName || '',
        'ItemUnit': fd.unit || '',
        'ItemQuantity': fd.quantity || '',
        'خام بکری': parseFloat(fd.khamBakri) || 0,
        'لاگا': parseFloat(fd.laga) || 0, // Laga is saved but NOT included in expenses calculation
        'اخراجات': totalExpenses, // Set akhrajat to calculated total (excluding laga)
        'پختہ بکری': parseFloat(fd.pakhtaBakri) || 0,
        'نقدی': expenseFieldsForCalculation['نقدی'],
        'کرایه': expenseFieldsForCalculation['کرایه'],
        'کمیشن': expenseFieldsForCalculation['کمیشن'],
        'مزدوری': expenseFieldsForCalculation['مزدوری'],
        'مشیانه': expenseFieldsForCalculation['مشیانه'],
        'مارکیٹ فیس': expenseFieldsForCalculation['مارکیٹ فیس']
      };

      // Prepare sales object for bill (using item data from top section)
      const sales = {
        'Raw Sale': parseFloat(fd.khamBakri) || 0
      };

      // Store the serial number to use for all sales created from this bill
      const billSerialNumber = fd.serialNumber;

      // Create bill payload
      const billPayload = {
        serialNumber: billSerialNumber,
        voucherNumber: billSerialNumber, // Voucher number same as serial number
        customerPartyId: fd.supplierPartyId, // Using supplier as customer for bill - required for supplier khata
        customerName: fd.supplierName || '',
        customerNameUrdu: fd.supplierNameUrdu || '',
        date: fd.date,
        expenses: expenses,
        sales: sales,
        receivedMessage: 'Received, thanks for selling',
        receivedMessageUrdu: 'وصول ہوا ہے بیجنے کا شکریہ',
        goodsMessage: 'Your goods via consignment/bill of lading',
        goodsMessageUrdu: 'آپکا مال بذریعہ بلٹی'
      };

      // Debug log to verify customerPartyId is being sent
      console.log('Bill Payload:', {
        customerPartyId: billPayload.customerPartyId,
        customerName: billPayload.customerName,
        customerNameUrdu: billPayload.customerNameUrdu
      });

      // Create or update bill
      let billResponse;
      if (isEditMode && editBillId) {
        // Update existing bill
        billResponse = await api.put(`/bill/company/${editBillId}`, billPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new bill
        billResponse = await api.post("/bill/company/create", billPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      const createdBill = billResponse.data?.bill;

      // Create or update sales from purchase rows
      // In edit mode: create new sales for rows without saleId, update existing ones
      // In create mode: create all sales
      const createdSales = [];
      const updatedSales = [];
      const deletedSales = [];
      const rowsWithoutCustomer = [];

      // Track which sales still exist (for deletion detection)
      const currentSaleIds = new Set();

      for (let i = 0; i < purchaseRows.length; i++) {
        const row = purchaseRows[i];

        // Skip rows without customer
        if (!row.customerPartyId) {
          rowsWithoutCustomer.push(i + 1);
          continue;
        }

        // Track existing sale IDs
        if (row.saleId) {
          currentSaleIds.add(row.saleId.toString());
        }

        // Prepare sale items for this customer
        // Use item name from upper section (چیز کا نام) as the item name
        const saleItems = [];
        if (fd.itemName && row.quantity && row.unitPrice) {
          saleItems.push({
            description: fd.itemName,
            descriptionUrdu: fd.itemName,
            unit: row.unit || '',
            dana: parseFloat(row.dana) || null,
            quantity: parseFloat(row.quantity) || 0,
            ratePerUnit: parseFloat(row.unitPrice) || 0,
            lineTotal: parseFloat(row.total) || 0
          });
        }

        // Create sale payload with supplier info from upper section and customer from row
        // Use the same serial number as the bill (all sales from this bill will have same number)
        const salePayload = {
          serialNumber: billSerialNumber, // Same as bill serial number
          supplierName: fd.supplierName || '', // Supplier from upper section
          supplierNameUrdu: fd.supplierNameUrdu || '', // Supplier from upper section
          supplierVehicleNumber: fd.vehicleNumber || '',
          sellerName: row.customerName || '', // Customer name (seller)
          sellerNameUrdu: row.customerNameUrdu || '', // Customer name Urdu (seller)
          sellerNumber: '',
          customerPartyId: row.customerPartyId, // Customer goes to customer khata
          items: saleItems,
          totalItems: saleItems.length,
          subtotal: parseFloat(row.total) || 0,
          discount: 0,
          totalAmount: parseFloat(row.total) || 0,
          paymentMethodId: null,
          paymentMethod: '',
          amountPaid: parseFloat(row.paidAmount) || 0,
          date: fd.date
        };

        if (isEditMode && row.saleId) {
          // Update existing sale
          try {
            const saleResponse = await api.put(`/sale/company/${row.saleId}`, salePayload, {
              headers: { Authorization: `Bearer ${token}` }
            });
            updatedSales.push(saleResponse.data?.sale);
          } catch (error) {
            console.error(`Failed to update sale ${row.saleId}:`, error);
            // If update fails, try creating a new one
            try {
              const saleResponse = await api.post("/sale/company/create", salePayload, {
                headers: { Authorization: `Bearer ${token}` }
              });
              createdSales.push(saleResponse.data?.sale);
            } catch (createError) {
              console.error(`Failed to create sale after update failed:`, createError);
            }
          }
        } else {
          // Create new sale (either in create mode or edit mode for new rows)
          try {
            const saleResponse = await api.post("/sale/company/create", salePayload, {
              headers: { Authorization: `Bearer ${token}` }
            });
            createdSales.push(saleResponse.data?.sale);
          } catch (error) {
            console.error(`Failed to create sale:`, error);
          }
        }
      }

      // In edit mode, delete sales that were removed from purchaseRows
      if (isEditMode && originalSalesRef.current.length > 0) {
        for (const originalSaleId of originalSalesRef.current) {
          if (!currentSaleIds.has(originalSaleId.toString())) {
            // This sale was removed, delete it
            try {
              await api.delete(`/sale/company/${originalSaleId}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              deletedSales.push(originalSaleId);
            } catch (error) {
              console.error(`Failed to delete sale ${originalSaleId}:`, error);
            }
          }
        }
      }

      // Show warning if some rows don't have customers
      if (rowsWithoutCustomer.length > 0) {
        toast.warning(companyLanguage === 'ur'
          ? `قطار ${rowsWithoutCustomer.join(', ')} میں گاہک کا نام نہیں ہے - ان کی سیل رسیپٹ نہیں بنی`
          : `Rows ${rowsWithoutCustomer.join(', ')} are missing customer name - sale receipts not created`);
      }

      // Show success message
      if (isEditMode) {
        const messages = [];
        if (createdSales.length > 0) {
          messages.push(companyLanguage === 'ur' ? `${createdSales.length} نئی سیل بنی` : `${createdSales.length} new sales created`);
        }
        if (updatedSales.length > 0) {
          messages.push(companyLanguage === 'ur' ? `${updatedSales.length} سیل اپ ڈیٹ ہوئی` : `${updatedSales.length} sales updated`);
        }
        if (deletedSales.length > 0) {
          messages.push(companyLanguage === 'ur' ? `${deletedSales.length} سیل ختم ہوئی` : `${deletedSales.length} sales deleted`);
        }

        if (messages.length > 0) {
          toast.success(companyLanguage === 'ur'
            ? `بل اپ ڈیٹ ہو گیا! ${messages.join(', ')}`
            : `Bill updated! ${messages.join(', ')}`);
        } else {
          toast.success(companyLanguage === 'ur' ? 'بل اپ ڈیٹ ہو گیا!' : 'Bill updated successfully!');
        }
      } else {
        toast.success(companyLanguage === 'ur' ? 'بل اور سیل بن گئے!' : 'Bill and sales created successfully!');
      }

      // Redirect to bill view - always navigate after save
      const billId = createdBill?._id || editBillId;
      if (billId) {
        navigate(`/bill/view/${billId}`);
      } else {
        // Fallback: if no bill ID available, go to dashboard
        navigate('/company/dashboard-part2');
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.message || (companyLanguage === 'ur' ? 'محفوظ کرنے میں خرابی' : 'Failed to save'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gray-100 overflow-x-hidden"
      style={{
        margin: 0,
        padding: 0,
        width: '100vw',
        maxWidth: '100vw',
        position: 'relative',
        left: 0,
        right: 0,
        overflowY: 'auto'
      }}
      dir={companyLanguage === "ur" ? "rtl" : "ltr"}
    >
      {/* HEADER */}
      <DashboardHeader
        company={company}
        companyLanguage={companyLanguage}
        onLogout={() => {
          localStorage.removeItem("companyToken");
          localStorage.removeItem("companyData");
          navigate("/company/login");
        }}
      />

      {/* PAGE CONTENT */}
      <div
        className="w-full py-4"
        style={{
          paddingLeft: '10px',
          paddingRight: '10px',
          width: '100%',
          maxWidth: '100%',
          margin: 0
        }}
      >
        {/* BILL CONTAINER */}
        <div className="bg-white shadow-md w-full p-4 border-2 border-black" style={{ borderRadius: 0, width: '100%' }}>

          {/* TITLE */}
          <h1 className="text-3xl font-bold text-center mb-4 text-green-800">
            بل بنائیں
          </h1>

          {/* FIELDS (2 ROWS ONLY) */}
          <div className="space-y-4 mb-4 pb-4 border-b-2 border-black">
            {/* ===== ROW 1 ===== */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-bold text-right">تاریخ -</label>
                <input
                  ref={dateRef}
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  onKeyDown={(e) => handleEnterKey(e, serialNumberRef)}
                  className="w-full border border-black rounded px-2 py-2"
                />
              </div>

              {/* Serial Number */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-bold text-right">سیریل نمبر -</label>
                <input
                  ref={serialNumberRef}
                  type="text"
                  value={formData.serialNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, serialNumber: e.target.value }))}
                  onKeyDown={(e) => handleEnterKey(e, supplierSearchInputRef)}
                  className="w-full border border-black rounded px-2 py-2"
                  dir="ltr"
                />
              </div>

              {/* Supplier Name */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-bold text-right">سپلائر کا نام -</label>
                <div className="relative w-full" ref={supplierDropdownRef}>
                  <input
                    ref={supplierSearchInputRef}
                    type="text"
                    value={formData.supplierPartyId
                      ? (supplierParties.find(p => p._id === formData.supplierPartyId)?.nameUrdu ||
                        supplierParties.find(p => p._id === formData.supplierPartyId)?.name || '')
                      : supplierSearchTerm}
                    onChange={(e) => {
                      // Same as recovery: just set search term and open dropdown
                      setSupplierSearchTerm(e.target.value);
                      setShowSupplierDropdown(true);
                    }}
                    onClick={() => {
                      // Open Urdu keyboard on click (same as recovery)
                      setShowSupplierUrduKeyboard(true);
                      setShowSupplierDropdown(true);
                    }}
                    onFocus={() => {
                      // Open Urdu keyboard on focus (same as recovery)
                      setShowSupplierUrduKeyboard(true);
                      setShowSupplierDropdown(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !showSupplierDropdown) {
                        e.preventDefault();
                        vehicleNumberRef.current?.focus();
                      }
                    }}
                    className="w-full border border-black rounded px-2 py-2"
                    placeholder="سپلائر تلاش کریں..."
                    style={{
                      fontFamily: companyLanguage === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" : '',
                      lineHeight: companyLanguage === 'ur' ? '1.8' : '1.5',
                      paddingTop: companyLanguage === 'ur' ? '12px' : '8px',
                      paddingBottom: companyLanguage === 'ur' ? '12px' : '8px',
                      fontSize: '13px',
                      height: 'auto',
                      minHeight: '44px',
                      verticalAlign: 'middle',
                      overflow: 'visible'
                    }}
                  />
                  {showSupplierDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-black rounded shadow-lg max-h-60 overflow-y-auto" dir="rtl">
                      {filteredSuppliers.map(party => (
                        <div
                          key={party._id}
                          onClick={() => handleSupplierSelect(party._id)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-gray-700 text-right border-b border-gray-100 last:border-b-0"
                        >
                          {party.nameUrdu || party.name}
                        </div>
                      ))}
                      {filteredSuppliers.length === 0 && supplierSearchTerm && (
                        <div className="px-3 py-2 text-gray-500 text-center">
                          {companyLanguage === 'ur' ? 'کوئی سپلائر نہیں ملا' : 'No supplier found'}
                        </div>
                      )}
                    </div>
                  )}
                  {showSupplierUrduKeyboard && (
                    <UrduKeyboard
                      isVisible={showSupplierUrduKeyboard}
                      onKeyPress={handleSupplierSearchUrduKeyPress}
                      onClose={() => setShowSupplierUrduKeyboard(false)}
                      inputRef={supplierSearchInputRef}
                    />
                  )}
                </div>
              </div>

              {/* Vehicle Number */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-bold text-right">گاڑی نمبر -</label>
                <input
                  ref={vehicleNumberRef}
                  type="text"
                  value={formData.vehicleNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicleNumber: e.target.value }))}
                  placeholder="گاڑی نمبر"
                  onKeyDown={(e) => handleEnterKey(e, itemNameRef)}
                  className="w-full border border-black rounded px-2 py-2"
                  dir="ltr"
                />
              </div>
            </div>

            {/* ===== ROW 2 ===== */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Item Name */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-right">
                  چیز کا نام - <span className="text-red-500">*</span>
                </label>
                {isStaticBillForm ? (
                  <input
                    ref={itemNameRef}
                    type="text"
                    readOnly
                    value={formData.itemName}
                    onKeyDown={(e) => handleEnterKey(e, unitSelectRef)}
                    className="w-full border border-black rounded px-2 py-2 bg-gray-100 text-gray-800 cursor-default"
                    title={companyLanguage === 'ur' ? 'پروفائل میں "ایک ثابت" موڈ سے تبدیل کریں' : 'Change in profile (static mode)'}
                    style={{
                      fontFamily: companyLanguage === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" : '',
                      lineHeight: companyLanguage === 'ur' ? '1.8' : '1.5',
                      paddingTop: companyLanguage === 'ur' ? '12px' : '8px',
                      paddingBottom: companyLanguage === 'ur' ? '12px' : '8px',
                      fontSize: '13px',
                      minHeight: '44px'
                    }}
                  />
                ) : billItemSelectOptionsWithCurrent.length > 0 ? (
                  <select
                    ref={itemNameRef}
                    value={formData.itemName}
                    onChange={(e) => {
                      const selectedItem = e.target.value;
                      const option = (company.billItemOptions || []).find((opt) => opt.name === selectedItem);
                      setFormData((prev) => ({
                        ...prev,
                        itemName: selectedItem,
                        unit: option && option.unit ? option.unit : prev.unit
                      }));
                    }}
                    onKeyDown={(e) => handleEnterKey(e, unitSelectRef)}
                    className="w-full border border-black rounded px-2 py-2"
                    style={{
                      fontFamily: companyLanguage === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" : '',
                      lineHeight: companyLanguage === 'ur' ? '1.8' : '1.5',
                      paddingTop: companyLanguage === 'ur' ? '12px' : '8px',
                      paddingBottom: companyLanguage === 'ur' ? '12px' : '8px',
                      fontSize: '13px',
                      height: 'auto',
                      minHeight: '44px'
                    }}
                  >
                    <option value="">{companyLanguage === 'ur' ? 'نام منتخب کریں' : 'Select Item'}</option>
                    {billItemSelectOptionsWithCurrent.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    ref={itemNameRef}
                    type="text"
                    value={formData.itemName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, itemName: e.target.value }))}
                    onKeyDown={(e) => handleEnterKey(e, unitSelectRef)}
                    className="w-full border border-black rounded px-2 py-2"
                  />
                )}
              </div>

              {/* Unit */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-right">یونٹ <span className="text-red-500">*</span></label>
                {isStaticBillForm ? (
                  <input
                    ref={unitSelectRef}
                    type="text"
                    readOnly
                    value={formData.unit}
                    onKeyDown={(e) => handleEnterKey(e, quantityRef)}
                    className="w-full border border-black rounded px-2 py-2 bg-gray-100 text-gray-800 cursor-default"
                    title={companyLanguage === 'ur' ? 'پروفائل میں ترمیم کریں' : 'Edit in profile'}
                    style={{
                      fontFamily: companyLanguage === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" : '',
                      lineHeight: companyLanguage === 'ur' ? '1.8' : '1.5',
                      paddingTop: companyLanguage === 'ur' ? '12px' : '8px',
                      paddingBottom: companyLanguage === 'ur' ? '12px' : '8px',
                      fontSize: '13px',
                      minHeight: '44px'
                    }}
                  />
                ) : (
                  <select
                    ref={unitSelectRef}
                    value={formData.unit}
                    onChange={(e) => setFormData((prev) => ({ ...prev, unit: e.target.value }))}
                    onKeyDown={(e) => handleEnterKey(e, quantityRef)}
                    className="w-full border border-black rounded px-2 py-2"
                    style={{
                      fontFamily: companyLanguage === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" : '',
                      lineHeight: companyLanguage === 'ur' ? '1.8' : '1.5',
                      paddingTop: companyLanguage === 'ur' ? '12px' : '8px',
                      paddingBottom: companyLanguage === 'ur' ? '12px' : '8px',
                      fontSize: '13px',
                      minHeight: '44px'
                    }}
                  >
                    <option value="">{companyLanguage === 'ur' ? 'یونٹ منتخب کریں' : 'Select unit'}</option>
                    {mergedBillUnits.map((u) => (
                      <option key={u} value={u}>
                        {companyLanguage === 'ur' ? (BILL_UNIT_LABELS_UR[u] || u) : u}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Quantity */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-bold text-right">مقدار</label>
                <input
                  ref={quantityRef}
                  type="text"
                  value={formData.quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, quantity: e.target.value }))}
                  onKeyDown={(e) => handleEnterKey(e, commissionRef)}
                  className="w-full border border-black rounded px-2 py-2"
                  dir="ltr"
                />
              </div>

              {/* Commission Percentage */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-bold text-right">کمیشن فیصد</label>
                <input
                  ref={commissionRef}
                  type="text"
                  value={formData.commission}
                  onChange={(e) => handleCommissionPercentageChange(e.target.value)}
                  placeholder="مثلاً 5"
                  onKeyDown={(e) => handleEnterKey(e, khamBakriRef)}
                  className="w-full border border-black rounded px-2 py-2"
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          {/* EXPENSE DETAILS SECTION */}
          <div className="flex flex-col gap-6 mt-6">
            {/* LEFT SECTION: Expenses */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-green-800 mb-4 text-center">اخراجات</h2>

              {/* All fields in one row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* خام بکری */}
                <div className="flex flex-col gap-2 items-center">
                  <label className="text-sm font-bold text-red-600">
                    خام بکری <span className="text-red-500">*</span>
                  </label>
                  <input
                    ref={khamBakriRef}
                    type="text"
                    value={formData.khamBakri}
                    onChange={(e) => setFormData(prev => ({ ...prev, khamBakri: e.target.value }))}
                    onKeyDown={(e) => handleEnterKey(e, lagaRef)}
                    className="w-full border border-black rounded px-2 py-2"
                    dir="ltr"
                  />
                </div>

                {/* اخراجات (Total Expenses) - read only, calculated */}
                <div className="flex flex-col gap-2 items-center">
                  <label className="text-sm font-bold text-red-600 whitespace-nowrap">اخراجات</label>
                  <div className="w-full border border-black rounded px-2 py-2 bg-gray-50 text-gray-700 text-right">
                    {totalExpenses.toLocaleString()}
                  </div>
                </div>

                {/* پختہ بکری (Net Sale) - read only, calculated */}
                <div className="flex flex-col gap-2 items-center">
                  <label className="text-sm font-bold text-red-600 whitespace-nowrap">پختہ بکری</label>
                  <div className="w-full border border-black rounded px-2 py-2 bg-gray-50 text-gray-700 text-right">
                    {netSale.toLocaleString()}
                  </div>
                </div>

                {/* لاگا */}
                <div className="flex flex-col gap-2 items-center">
                  <label className="text-sm font-bold text-red-600 whitespace-nowrap">لاگا</label>
                  <input
                    ref={lagaRef}
                    type="text"
                    value={formData.laga}
                    onChange={(e) => setFormData(prev => ({ ...prev, laga: e.target.value }))}
                    onKeyDown={(e) => handleEnterKey(e, naqdiRef)}
                    className="w-full border border-black rounded px-2 py-2"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>

            {/* RIGHT SECTION: Expense Details */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-green-800 mb-4 text-center">تفصیل اخراجات -</h2>

              {/* Fields in 2 rows and 3 columns */}
              <div className="space-y-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Column 1: نقدی */}
                  <div className="flex items-center justify-center gap-4">
                    <label className="text-sm font-bold text-red-600 whitespace-nowrap">نقدی.</label>
                    <input
                      ref={naqdiRef}
                      type="text"
                      value={formData.naqdi}
                      onChange={(e) => setFormData(prev => ({ ...prev, naqdi: e.target.value }))}
                      onKeyDown={(e) => handleEnterKey(e, karayaRef)}
                      className="w-full border border-black rounded px-2 py-2"
                      dir="ltr"
                    />
                  </div>

                  {/* Column 2: کرایه */}
                  <div className="flex items-center justify-center gap-4">
                    <label className="text-sm font-bold text-red-600 whitespace-nowrap">
                      کرایه <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={karayaRef}
                      type="text"
                      value={formData.karaya}
                      onChange={(e) => setFormData(prev => ({ ...prev, karaya: e.target.value }))}
                      onKeyDown={(e) => handleEnterKey(e, commissionDetailRef)}
                      className="w-full border border-black rounded px-2 py-2"
                      dir="ltr"
                    />
                  </div>

                  {/* Column 3: کمیشن */}
                  <div className="flex items-center justify-center gap-4">
                    <label className="text-sm font-bold text-red-600 whitespace-nowrap">
                      کمیشن <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={commissionDetailRef}
                      type="text"
                      value={formData.commissionDetail}
                      onChange={(e) => handleCommissionAmountChange(e.target.value)}
                      onKeyDown={(e) => handleEnterKey(e, mazdooriRef)}
                      className="w-full border border-black rounded px-2 py-2"
                      dir="ltr"
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Column 1: مزدوری */}
                  <div className="flex items-center justify-center gap-4">
                    <label className="text-sm font-bold text-red-600 whitespace-nowrap">
                      مزدوری. <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={mazdooriRef}
                      type="text"
                      value={formData.mazdoori}
                      onChange={(e) => setFormData(prev => ({ ...prev, mazdoori: e.target.value }))}
                      onKeyDown={(e) => handleEnterKey(e, mashianaRef)}
                      className="w-full border border-black rounded px-2 py-2"
                      dir="ltr"
                    />
                  </div>

                  {/* Column 2: مشیانه */}
                  <div className="flex items-center justify-center gap-4">
                    <label className="text-sm font-bold text-red-600 whitespace-nowrap">
                      مشیانه. <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={mashianaRef}
                      type="text"
                      value={formData.mashiana}
                      onChange={(e) => setFormData(prev => ({ ...prev, mashiana: e.target.value }))}
                      onKeyDown={(e) => handleEnterKey(e, barketFeeRef)}
                      className="w-full border border-black rounded px-2 py-2"
                      dir="ltr"
                    />
                  </div>

                  {/* Column 3: مارکیٹ فیس */}
                  <div className="flex items-center justify-center gap-4">
                    <label className="text-sm font-bold text-red-600 whitespace-nowrap">
                      مارکیٹ فیس. <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={barketFeeRef}
                      type="text"
                      value={formData.barketFee}
                      onChange={(e) => setFormData(prev => ({ ...prev, barketFee: e.target.value }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // Focus on first table row customer name field if table exists
                          const firstTableInput = document.querySelector('table tbody tr:first-child td:first-child input');
                          if (firstTableInput) {
                            firstTableInput.focus();
                          }
                        }
                      }}
                      className="w-full border border-black rounded px-2 py-2"
                      dir="ltr"
                    />
                  </div>
                </div>
              </div>

              {/* Total Amount (کل رقم) - shows Total Expenses */}
              <div className="border-t border-b border-black pt-2 pb-2 mt-4">
                <div className="flex items-center gap-4">
                  <label className="text-sm font-bold text-red-600 whitespace-nowrap">کل رقم -</label>
                  <input
                    type="text"
                    value={formData.editableTotalExpenses !== '' ? formData.editableTotalExpenses : (totalExpenses > 0 ? totalExpenses.toLocaleString() : '')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '');
                      totalExpensesManuallyEditedRef.current = true;
                      setFormData(prev => ({ ...prev, editableTotalExpenses: value }));
                    }}
                    onBlur={() => {
                      // If empty, reset to calculated value
                      if (!formData.editableTotalExpenses || formData.editableTotalExpenses === '') {
                        totalExpensesManuallyEditedRef.current = false;
                        setFormData(prev => ({ ...prev, editableTotalExpenses: totalExpenses > 0 ? totalExpenses.toLocaleString() : '' }));
                      }
                    }}
                    className="text-lg font-bold text-black border border-gray-300 rounded px-2 py-1 bg-white min-w-[120px]"
                    placeholder={totalExpenses > 0 ? totalExpenses.toLocaleString() : '0'}
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SALE CREATE FORM SECTION */}
          <div className="mt-8 border-t-2 border-black pt-6">
            <h2 className="text-2xl font-bold text-green-800 mb-6 text-center">سیل فارم</h2>

            {/* Items Table with Summary Columns */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">خریداری کی تفصیل</h3>
              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '600px', minHeight: '400px' }}>
                <table className="min-w-full border border-black">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 border border-black text-right text-sm font-medium text-gray-700">گاہک کا نام</th>
                      <th className="px-4 py-2 border border-black text-right text-sm font-medium text-gray-700">تفصیل</th>
                      <th className="px-4 py-2 border border-black text-center text-sm font-medium text-gray-700">یونٹ</th>
                      <th className="px-4 py-2 border border-black text-center text-sm font-medium text-gray-700">دانہ</th>
                      <th className="px-4 py-2 border border-black text-center text-sm font-medium text-gray-700">مقدار</th>
                      <th className="px-4 py-2 border border-black text-center text-sm font-medium text-gray-700">فی یونٹ قیمت</th>
                      <th className="px-4 py-2 border border-black text-center text-sm font-medium text-gray-700">کل رقم</th>
                      <th className="px-4 py-2 border border-black text-center text-sm font-medium text-gray-700">ادا کی گئی رقم</th>
                      <th className="px-4 py-2 border border-black text-center text-sm font-medium text-gray-700">عمل</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseRows.map((row, rowIndex) => (
                      <tr key={row.id}>
                        <td className="px-4 py-2 border border-black" style={{ overflow: 'visible' }}>
                          <div className="relative" ref={(el) => purchaseCustomerDropdownRefs.current[row.id] = el} style={{ overflow: 'visible' }}>
                            <input
                              ref={el => {
                                purchaseCustomerInputRefs.current[row.id] = el;
                              }}
                              type="text"
                              value={row.customerPartyId
                                ? (ledgerParties.find(p => p._id === row.customerPartyId)?.nameUrdu ||
                                  ledgerParties.find(p => p._id === row.customerPartyId)?.name || '')
                                : row.customerSearchTerm}
                              onChange={(e) => handlePurchaseCustomerSearch(row.id, e.target.value)}
                              onKeyDown={(e) => {
                                const filteredParties = getFilteredCustomersForRow(row.customerSearchTerm);

                                if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  if (filteredParties.length > 0) {
                                    const currentIndex = purchaseRowHighlightedIndex[row.id] || -1;
                                    const newIndex = currentIndex < filteredParties.length - 1
                                      ? currentIndex + 1
                                      : 0;
                                    setPurchaseRowHighlightedIndex(prev => ({
                                      ...prev,
                                      [row.id]: newIndex
                                    }));
                                    // Scroll highlighted item into view
                                    setTimeout(() => {
                                      const itemRef = purchaseCustomerListRefs.current[`${row.id}_${filteredParties[newIndex]?._id}`];
                                      if (itemRef) {
                                        itemRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                      }
                                    }, 0);
                                  }
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  if (filteredParties.length > 0) {
                                    const currentIndex = purchaseRowHighlightedIndex[row.id] || -1;
                                    const newIndex = currentIndex > 0
                                      ? currentIndex - 1
                                      : filteredParties.length - 1;
                                    setPurchaseRowHighlightedIndex(prev => ({
                                      ...prev,
                                      [row.id]: newIndex
                                    }));
                                    // Scroll highlighted item into view
                                    setTimeout(() => {
                                      const itemRef = purchaseCustomerListRefs.current[`${row.id}_${filteredParties[newIndex]?._id}`];
                                      if (itemRef) {
                                        itemRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                      }
                                    }, 0);
                                  }
                                } else if (e.key === 'Enter') {
                                  if (row.showDropdown && (purchaseRowHighlightedIndex[row.id] ?? -1) >= 0 && filteredParties[purchaseRowHighlightedIndex[row.id]]) {
                                    e.preventDefault();
                                    handlePurchaseCustomerSelect(row.id, filteredParties[purchaseRowHighlightedIndex[row.id]]._id);
                                    setPurchaseRowHighlightedIndex(prev => ({
                                      ...prev,
                                      [row.id]: -1
                                    }));
                                  } else if (!row.showDropdown) {
                                    handleTableEnterKey(e, rowIndex, 0);
                                  }
                                }
                              }}
                              onClick={() => {
                                // Open Urdu keyboard on click if field is empty, otherwise toggle
                                if (!row.customerSearchTerm || row.customerSearchTerm.trim() === '') {
                                  setPurchaseRowUrduKeyboard(row.id);
                                } else {
                                  setPurchaseRowUrduKeyboard(prev => prev === row.id ? null : row.id);
                                }
                                // Open dropdown on click (same as recovery)
                                togglePurchaseDropdown(row.id, true);
                              }}
                              onFocus={() => {
                                // Open Urdu keyboard on focus if field is empty
                                if (!row.customerSearchTerm || row.customerSearchTerm.trim() === '') {
                                  setPurchaseRowUrduKeyboard(row.id);
                                }
                                // Open dropdown on focus (same as recovery)
                                togglePurchaseDropdown(row.id, true);
                              }}
                              className="w-full px-2 py-1 border border-black rounded"
                              placeholder="گاہک تلاش کریں..."
                              style={{
                                fontFamily: companyLanguage === 'ur' ? "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" : '',
                                lineHeight: companyLanguage === 'ur' ? '1.8' : '1.5',
                                fontSize: '13px',
                                height: 'auto',
                                minHeight: '32px',
                                verticalAlign: 'middle',
                                overflow: 'visible'
                              }}
                            />
                            {row.showDropdown && (
                              <div
                                className="absolute z-[9999] w-full mt-1 bg-white border border-black rounded shadow-lg overflow-y-auto"
                                dir="rtl"
                                style={{
                                  right: 0,
                                  top: '100%',
                                  maxHeight: '300px',
                                  minHeight: '100px'
                                }}
                              >
                                {getFilteredCustomersForRow(row.customerSearchTerm).map((party, index) => {
                                  const isHighlighted = (purchaseRowHighlightedIndex[row.id] ?? -1) === index;
                                  return (
                                    <div
                                      key={party._id}
                                      ref={(el) => {
                                        if (el) {
                                          purchaseCustomerListRefs.current[`${row.id}_${party._id}`] = el;
                                        }
                                      }}
                                      onClick={() => {
                                        handlePurchaseCustomerSelect(row.id, party._id);
                                        setPurchaseRowHighlightedIndex(prev => ({
                                          ...prev,
                                          [row.id]: -1
                                        }));
                                      }}
                                      className={`px-3 py-2 cursor-pointer text-gray-700 text-right border-b border-gray-100 last:border-b-0 ${isHighlighted
                                        ? 'bg-blue-100 border-2 border-blue-300'
                                        : 'hover:bg-gray-100'
                                        }`}
                                    >
                                      {party.nameUrdu || party.name}
                                    </div>
                                  );
                                })}
                                {getFilteredCustomersForRow(row.customerSearchTerm).length === 0 && row.customerSearchTerm && (
                                  <div className="px-3 py-2 text-gray-500 text-center">
                                    {companyLanguage === 'ur' ? 'کوئی گاہک نہیں ملا' : 'No customer found'}
                                  </div>
                                )}
                              </div>
                            )}
                            {purchaseRowUrduKeyboard === row.id && (
                              <UrduKeyboard
                                isVisible={purchaseRowUrduKeyboard === row.id}
                                onKeyPress={(key) => handlePurchaseCustomerUrduKeyPress(key, row.id)}
                                onClose={() => setPurchaseRowUrduKeyboard(null)}
                                inputRef={purchaseCustomerInputRefs.current[row.id]}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-2 border border-black">
                          <input
                            type="text"
                            value={row.detail}
                            onChange={(e) => handlePurchaseRowChange(row.id, 'detail', e.target.value)}
                            onKeyDown={(e) => handleTableEnterKey(e, rowIndex, 1)}
                            className="w-full px-2 py-1 border border-black rounded"
                            placeholder="آئٹم کا نام"
                          />
                        </td>
                        <td className="px-4 py-2 border border-black">
                          <input
                            list="table-unit-options"
                            value={row.unit}
                            onChange={(e) => handlePurchaseRowChange(row.id, 'unit', e.target.value)}
                            onKeyDown={(e) => handleTableEnterKey(e, rowIndex, 2)}
                            className="w-full px-2 py-1 border border-black rounded text-center"
                            placeholder="یونٹ"
                          />
                          <datalist id="table-unit-options">
                            {mergedBillUnits.map((u) => (
                              <option key={u} value={u}>
                                {companyLanguage === 'ur' ? (BILL_UNIT_LABELS_UR[u] || u) : u}
                              </option>
                            ))}
                          </datalist>
                        </td>
                        <td className="px-4 py-2 border border-black">
                          <input
                            type="text"
                            value={row.dana}
                            onChange={(e) => handlePurchaseRowChange(row.id, 'dana', e.target.value)}
                            onKeyDown={(e) => handleTableEnterKey(e, rowIndex, 3)}
                            className="w-full px-2 py-1 border border-black rounded text-center"
                            placeholder="دانہ"
                            dir="ltr"
                          />
                        </td>
                        <td className="px-4 py-2 border border-black">
                          <input
                            type="text"
                            value={row.quantity}
                            onChange={(e) => handlePurchaseRowChange(row.id, 'quantity', e.target.value)}
                            onKeyDown={(e) => handleTableEnterKey(e, rowIndex, 4)}
                            className="w-full px-2 py-1 border border-black rounded text-center"
                            placeholder=""
                            dir="ltr"
                          />
                        </td>
                        <td className="px-4 py-2 border border-black">
                          <input
                            type="text"
                            value={row.unitPrice}
                            onChange={(e) => handlePurchaseRowChange(row.id, 'unitPrice', e.target.value)}
                            onKeyDown={(e) => handleTableEnterKey(e, rowIndex, 5)}
                            className="w-full px-2 py-1 border border-black rounded text-center"
                            placeholder=""
                            dir="ltr"
                          />
                        </td>
                        <td className="px-4 py-2 border border-black text-center">
                          <input
                            type="text"
                            value={row.total || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              handlePurchaseRowChange(row.id, 'total', value);
                            }}
                            onBlur={() => {
                              // If empty, reset manual edit flag and recalculate
                              if (!row.total || row.total === '') {
                                purchaseRowTotalManuallyEditedRef.current[row.id] = false;
                                // Recalculate based on quantity and unitPrice
                                const qty = parseFloat(row.quantity) || 0;
                                const price = parseFloat(row.unitPrice) || 0;
                                const calculatedTotal = qty * price;
                                if (calculatedTotal > 0) {
                                  setPurchaseRows(prev =>
                                    prev.map(r => {
                                      if (r.id !== row.id) return r;
                                      return { ...r, total: calculatedTotal.toString() };
                                    })
                                  );
                                }
                              }
                            }}
                            onKeyDown={(e) => handleTableEnterKey(e, rowIndex, 6)}
                            className="w-full px-2 py-1 border border-black rounded text-center font-semibold bg-white"
                            placeholder="0"
                            dir="ltr"
                          />
                        </td>
                        <td className="px-4 py-2 border border-black">
                          <input
                            type="text"
                            value={row.paidAmount}
                            onChange={(e) => handlePurchaseRowChange(row.id, 'paidAmount', e.target.value)}
                            onKeyDown={(e) => handleTableEnterKey(e, rowIndex, 7)}
                            className="w-full px-2 py-1 border border-black rounded text-center"
                            placeholder=""
                            dir="ltr"
                          />
                        </td>
                        <td className="px-4 py-2 border border-black text-center">
                          <button
                            type="button"
                            onClick={() => handleDeletePurchaseRow(row.id)}
                            disabled={purchaseRows.length === 1}
                            className={`px-3 py-1 rounded text-white text-sm ${purchaseRows.length === 1
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-red-600 hover:bg-red-700'
                              }`}
                          >
                            قطار ختم کریں
                          </button>
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-gray-100 font-bold">
                      <td className="px-4 py-2 border border-black text-right" colSpan="6">
                        کل:
                      </td>
                      <td className="px-4 py-2 border border-black text-center">
                        <input
                          type="text"
                          value={formData.purchaseTotalAmount !== '' ? formData.purchaseTotalAmount : (purchaseTotalAmount > 0 ? purchaseTotalAmount.toFixed(2) : '')}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            purchaseTotalManuallyEditedRef.current = true;
                            setFormData(prev => ({ ...prev, purchaseTotalAmount: value }));
                          }}
                          onBlur={() => {
                            // If empty, reset to calculated value
                            if (!formData.purchaseTotalAmount || formData.purchaseTotalAmount === '') {
                              purchaseTotalManuallyEditedRef.current = false;
                              setFormData(prev => ({ ...prev, purchaseTotalAmount: purchaseTotalAmount > 0 ? purchaseTotalAmount.toFixed(2) : '' }));
                            }
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-center font-semibold bg-white"
                          placeholder={purchaseTotalAmount > 0 ? purchaseTotalAmount.toFixed(2) : '0'}
                          dir="ltr"
                        />
                      </td>
                      <td className="px-4 py-2 border border-black text-center font-semibold">
                        {purchaseTotalPaidAmount > 0 ? purchaseTotalPaidAmount.toFixed(2) : ''}
                      </td>
                      <td className="px-4 py-2 border border-black"></td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={handleAddPurchaseRow}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                  >
                    + مزید قطار شامل کریں
                  </button>
                </div>
              </div>
            </div>
          </div>


          {/* ACTION BUTTONS */}
          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              {isSaving ? (companyLanguage === 'ur' ? 'محفوظ ہو رہا ہے...' : 'Saving...') : (companyLanguage === 'ur' ? 'محفوظ کریں' : 'Save')}
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              پرنٹ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBillPart2;
